import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, KeyRound } from "lucide-react";
import { api, ApiError } from "../api";
import { Button, Field } from "../ui";
import {
  PasswordInput,
  PasswordPolicyChecklist,
  PasswordStrengthMeter,
  passwordMeetsPolicy,
} from "../components/PasswordInput";

/**
 * Landing page for the emailed password-reset link
 * (/reset-password?token=…). The token is single-use: a successful
 * submit burns it, sets the new password, and revokes every active
 * session for the account, so the user signs in fresh.
 */
export function ResetPasswordPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenDead, setTokenDead] = useState(false);

  const passwordsMatch = password === confirm;
  const canSubmit =
    passwordMeetsPolicy(password) && passwordsMatch && !busy && !!token;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (err) {
      if (err instanceof ApiError) {
        const code = (err.body as { error?: string })?.error;
        if (code === "token_invalid" || code === "token_expired") {
          setTokenDead(true);
          setError(
            code === "token_expired"
              ? "This reset link has expired."
              : "This reset link isn't valid any more — it may have been used already.",
          );
        } else if (code === "password_weak") {
          setError("That password doesn't meet the requirements below.");
        } else {
          setError("Reset failed. Please try again.");
        }
      } else {
        setError("Reset failed. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center -mt-6 -mx-4 sm:-mx-6 lg:-mx-8 py-10 px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-ink-800">Choose a new password</h1>
          <p className="mt-1.5 text-ink-500 font-light">
            You'll be signed out everywhere once it's set.
          </p>
        </div>
        <div className="card">
          {done ? (
            <div className="text-center py-4">
              <KeyRound size={28} className="mx-auto text-brand-700 mb-3" />
              <p className="text-sm text-ink-700 font-medium mb-1.5">
                Password updated
              </p>
              <p className="text-sm text-ink-500">
                Taking you to sign in… or{" "}
                <Link
                  to="/login"
                  className="font-medium text-brand-700 hover:text-brand-800"
                >
                  go there now
                </Link>
                .
              </p>
            </div>
          ) : !token || tokenDead ? (
            <div className="text-center py-4">
              <p className="text-sm text-ink-700 mb-2">
                {error ??
                  "This page needs the reset link from your email — the token is missing."}
              </p>
              <p className="text-sm text-ink-500">
                <Link
                  to="/forgot-password"
                  className="font-medium text-brand-700 hover:text-brand-800"
                >
                  Request a fresh reset link
                </Link>
                .
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-1">
              <Field label="New password">
                <PasswordInput
                  value={password}
                  onChange={setPassword}
                  minLength={12}
                  autoComplete="new-password"
                  required
                />
                <PasswordStrengthMeter value={password} />
                <PasswordPolicyChecklist value={password} />
              </Field>
              <Field
                label="Confirm new password"
                error={
                  confirm.length > 0 && !passwordsMatch
                    ? "Passwords don't match."
                    : undefined
                }
              >
                <PasswordInput
                  value={confirm}
                  onChange={setConfirm}
                  minLength={12}
                  autoComplete="new-password"
                  required
                />
              </Field>
              {error && <div className="alert-error">{error}</div>}
              <Button
                variant="primary"
                type="submit"
                className="w-full mt-2"
                loading={busy}
                disabled={!canSubmit}
              >
                {busy ? "Saving…" : "Set new password"}
                {!busy && <ArrowRight size={16} />}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
